import React, { useEffect, useRef, useState } from "react";
import { Modal, message, Button, Dropdown } from "antd";
import MaterialTable from "material-table";
import {
  CaretDownFilled,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  GetAppointments,
  UpdateAppointmentToCancelled,
  UpdateAppointmentToFinished,
} from "../../../utils/api";

import tableIcons from "../../../utils/tableIcons";
import { overlayContainer } from "../../../utils/overlayContainer";
// import Loader from "../../../components/shared/Loader";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { commonDateFormat } from "../../../utils/helper";

const dropdownItems = [
  {
    label: "Completed",
    key: "finished",
    disabled: false,
  },
  {
    label: "In Progress",
    key: "upcoming",
    disabled: false,
  },
  {
    label: "Cancelled",
    key: "cancelled",
    disabled: false,
  },
];

const AllAppointments = ({ appointmentsActiveTab }) => {
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const [modalData, setModalData] = useState({
    visible: false,
    name: null,
    id: null,
    action: null,
    selectedStatus: null,
  });
  const [query, setQuery] = useState({
    page: 0,
    pageSize: 25,
    search: "",
    orderField: "",
    orderDirection: "",
    customFilter: "",
  });
  const [, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  const columns = [
    {
      title: "Sr No.",
      field: "id",
      sorting: true,
      cellStyle: { width: "8%" },
    },
    {
      title: "Order ID",
      field: "uniqueId",
      sorting: true,
      cellStyle: { width: "10%" },
      render: (record) => {
        return (
          <span
            className="cursor-pointer"
            onClick={() => {
              navigate("/admin/appointments/" + record?.id);
            }}
          >
            #{record?.uniqueId || "--"}
          </span>
        );
      },
    },
    {
      title: "Service provider",
      field: "serviceProvider",
      sorting: false,
      cellStyle: { width: "15%" },
      render: (record) => {
        return <span>{record?.businessName || "--"}</span>;
      },
    },
    {
      title: "Client name",
      field: "clientName",
      sorting: false,
      cellStyle: { width: "15%" },
      render: (record) => {
        return (
          <span>
            {record?.isGuestUser
              ? "Guest"
              : record?.client?.name
              ? record?.client?.name
              : "--"}
          </span>
        );
      },
    },
    {
      title: "Date",
      field: "created_at",
      sorting: false,
      cellStyle: { width: "12%" },
      render: (record) => {
        return <span className="w-80%">{commonDateFormat(record?.date)}</span>;
      },
    },
    {
      title: "Time",
      field: "time",
      sorting: false,
      cellStyle: { width: "15%" },
      render: (record) => {
        const startTime = dayjs(record?.startTime, "HH:mm:ss").format("hh:mmA");
        const endTime = dayjs(record?.endTime, "HH:mm:ss").format("hh:mmA");
        return <span>{`${startTime} - ${endTime}` || "--"}</span>;
      },
    },
    {
      title: "Service",
      field: "service",
      sorting: false,
      cellStyle: { width: "15%" },
      render: (record) => {
        return <span>{record?.serviceName || "--"}</span>;
      },
    },
    {
      title: "Status",
      sorting: false,
      render: (record) => {
        let updatedItems = dropdownItems.filter((item) => {
          return item.key !== record.status;
        });
        return (
          <div className="cursor-pointer">
            {record?.status === "upcoming" ? (
              <Dropdown
                menu={{
                  items: updatedItems,
                  onClick: (e) =>
                    handleActionModalOpen("changeStatus", e?.key, record),
                }}
                trigger={["click"]}
              >
                <div className="flex items-center gap-x-1">
                  <span
                    className={`capitalize ${
                      record?.status === "finished"
                        ? "text-[green]"
                        : record?.status === "upcoming"
                        ? "text-yellow-500"
                        : "text-red-900"
                    }`}
                  >
                    {record?.status === "upcoming"
                      ? "Upcoming"
                      : record?.status === "finished"
                      ? "Completed"
                      : record?.status}
                  </span>
                  <button
                    className="text-[10px]"
                    onClick={(e) => e.preventDefault()}
                  >
                    <CaretDownFilled />
                  </button>
                </div>
              </Dropdown>
            ) : (
              <span
                className={`capitalize ${
                  record?.status === "finished"
                    ? "text-[green]"
                    : record?.status === "upcoming"
                    ? "text-yellow-500"
                    : "text-red-900"
                }`}
              >
                {record?.status === "upcoming"
                  ? "Upcoming"
                  : record?.status === "finished"
                  ? "Completed"
                  : record?.status}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const fetchData = async (dataquery) => {
    setLoading(true);

    let params = "paymentStatus=paid&status=upcoming,finished,cancelled";
    params += `&pageNo=${dataquery?.page + 1}`;
    params += `&limit=${dataquery?.pageSize}`;

    if (dataquery?.search) {
      params += `&search=${dataquery.search}`;
    }

    if (dataquery?.orderBy) {
      params += `&sort=${dataquery?.orderBy.field}&order=${dataquery?.orderDirection}`;
    }

    const response = await GetAppointments(params);

    setLoading(false);

    return {
      data: response?.items,
      page: dataquery?.page,
      totalCount: response?.totalRecords,
    };
  };

  const handleActionModalOpen = (action, status, data) => {
    if (action === "delete") {
      setModalData({
        visible: true,
        name: data.name,
        id: data.id,
        action: "delete",
        selectedStatus: null,
      });
    } else if (action === "changeStatus") {
      if (status === "finished") {
        const currentDate = new Date();
        const bookingStartTime = new Date(`${data?.date}T${data?.startTime}`);
        if (currentDate < bookingStartTime) {
          message.error(
            "Booking can't be completed because it has not been started yet."
          );
          return;
        }
      }
      setModalData({
        visible: true,
        name: data.name,
        id: data?.id,
        selectedStatus: status,
        action: action,
      });
    }
  };

  const handleChangeStatusOk = async () => {
    if (modalData.action === "changeStatus") {
      try {
        setBtnLoading(true);
        let response;
        if (modalData?.selectedStatus === "cancelled") {
          response = await UpdateAppointmentToCancelled(modalData?.id, {
            status: modalData?.selectedStatus,
          });
        } else {
          response = await UpdateAppointmentToFinished(modalData?.id, {
            status: modalData?.selectedStatus,
          });
        }
        setBtnLoading(false);
        if (response?.isSuccess) {
          message.success("Appointment status updated successfully");
          setModalData({
            selectedStatus: null,
            user: null,
            id: null,
            visible: false,
            action: null,
          });
          tableRef.current.onQueryChange();
        }
      } catch (error) {
        setBtnLoading(false);
        message.error("Error in updating status, Try again later");
      }
    }
  };

  const handleDeleteOk = async () => {
    // if (modalData.action === "delete") {
    //   try {
    //     setBtnLoading(true);
    //     const res = await DeletePromotion(modalData?.id);
    //     setBtnLoading(false);
    //     if (res?.isSuccess) {
    //       message.success("Promocode Deleted successfully");
    //       setModalData({
    //         id: null,
    //         visible: false,
    //         selectedStatus: null,
    //         user: null,
    //         action: null,
    //       });
    //       if (
    //         tableRef.current?.state?.query?.page + 1 ===
    //         tableRef.current?.state?.query?.totalCount
    //       ) {
    //         setQuery((prev) => ({ ...prev, page: prev.page - 1 }));
    //         tableRef.current.onQueryChange({ ...query, page: query.page - 1 });
    //       }
    //       tableRef.current.onQueryChange();
    //     }
    //   } catch (error) {
    //     setBtnLoading(false);
    //     message.error("Error in deletion, Try again later");
    //   }
    // }
  };

  const handleModalCancel = () => {
    setModalData({
      visible: false,
      selectedStatus: null,
      user: null,
      id: null,
      action: null,
    });
  };

  useEffect(() => {
    tableRef.current.onQueryChange();
  }, [appointmentsActiveTab]);

  return (
    <div className="searchposotion table-scroll poppins">
      <MaterialTable
        title="Completed Appointments"
        tableRef={tableRef}
        columns={columns}
        icons={tableIcons}
        page={query.page}
        options={{
          showTitle: false,
          draggable: false,
          paging: true,
          sorting: true,
          pageSize: Number(query.pageSize),
          pageSizeOptions: [10, 25, 50, 100],
          debounceInterval: 700,
          padding: "dense",
          paginationType: "stepped",
          searchFieldVariant: "outlined",
          searchFieldAlignment: "left",
        }}
        data={(query) => fetchData(query)}
        onChangeRowsPerPage={(pageSize) => {
          setQuery((prev) => ({ ...prev, pageSize: Number(pageSize) }));
        }}
        onChangePage={(page) =>
          setQuery((prev) => ({ ...prev, page: Number(page) }))
        }
        components={{
          Pagination: (props) => {
            const {
              page,
              rowsPerPage,
              count,
              onChangePage,
              onChangeRowsPerPage,
            } = props;
            return (
              <div
                className="box-shadow-pagination text-sm"
                style={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "10px 20px",
                  marginTop: "20px",
                }}
              >
                <div className="bottom-pagination-text">
                  <button
                    className="disabled:opacity-50"
                    onClick={(e) => {
                      onChangePage(e, page - 1);
                    }}
                    disabled={page === 0}
                  >
                    <LeftOutlined />
                  </button>
                  <span style={{ margin: "0 10px" }}>
                    {page + 1} of {Math.ceil(count / rowsPerPage)}
                  </span>
                  <button
                    className="disabled:opacity-50"
                    onClick={(e) => {
                      onChangePage(e, page + 1);
                    }}
                    disabled={
                      page === Math.ceil(count / rowsPerPage) - 1 ||
                      tableRef.current?.dataManager?.data?.length === 0
                    }
                  >
                    <RightOutlined />
                  </button>
                </div>
                <div style={{ position: "absolute", right: "25px" }}>
                  <span style={{ margin: "0 10px" }}>Items per page:</span>
                  <select
                    className="pagination-select"
                    disabled={tableRef.current?.dataManager?.data?.length === 0}
                    value={rowsPerPage}
                    onChange={(e) => {
                      onChangeRowsPerPage(e, e.target.value);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            );
          },
          OverlayLoading: () => {
            return <div style={overlayContainer}>{/* <Loader /> */}</div>;
          },
        }}
      />

      <Modal
        centered
        title={
          modalData.action === "delete"
            ? "Confirm Delete"
            : modalData?.action === "changeStatus"
            ? "Confirm Status Change"
            : "Confirm Selection Delete"
        }
        open={modalData.visible}
        onCancel={handleModalCancel}
        footer={
          <div>
            <Button onClick={handleModalCancel}>Cancel</Button>
            <Button
              type="primary"
              className="text-white"
              onClick={
                modalData.action === "delete"
                  ? handleDeleteOk
                  : handleChangeStatusOk
              }
            >
              Ok
              {btnLoading && (
                <span className="loader ml-2 align-middle mb-1"></span>
              )}
            </Button>
          </div>
        }
      >
        {modalData.action === "changeStatus"
          ? `Are you sure you want to change the status of this appointment to ${
              modalData.selectedStatus === "inProgress"
                ? "in progress"
                : modalData.selectedStatus
            }?`
          : `Are you sure you want to delete ${modalData?.name}?`}
      </Modal>
    </div>
  );
};

export default AllAppointments;
